import { ReactElement, useCallback, useState } from 'react';

import { TranslatableText } from '@dreryk/react-common';

import { NfzMfaTotpErrorSetter, nfzMfaTranslationKeys, useNfzMfaModalOpener } from '~libs.desktop.shared.account';

import { isEzwmMfaRequired, isNfzMfaLoginFault } from './EzwmMfaGuard.utils';

type OnMfaLogin = (mfaTotp: string) => Promise<void>;

type UseEzwmMfaGuardOptions = {
   blockingOperationLabel?: TranslatableText;
};

type UseEzwmMfaGuardReturn = {
   checkMfaRequired: () => Promise<boolean>;
   openMfaModal: (onLogin: OnMfaLogin) => void;
   renderMfaModal: () => ReactElement | null;
   mfaModalOpened: boolean;
};

export const useEzwmMfaGuard = (options: UseEzwmMfaGuardOptions = {}): UseEzwmMfaGuardReturn => {
   const { blockingOperationLabel } = options;
   const { openNfzMfaModal, closeNfzMfaModal, renderNfzMfaModal, nfzMfaModalOpened } = useNfzMfaModalOpener();
   const [onLoginCallback, setOnLoginCallback] = useState<OnMfaLogin | null>(null);

   const openMfaModal = useCallback(
      (onLogin: OnMfaLogin): void => {
         setOnLoginCallback(() => onLogin);
         openNfzMfaModal();
      },
      [openNfzMfaModal]
   );

   const closeMfaModal = useCallback((): void => {
      closeNfzMfaModal();
      setOnLoginCallback(null);
   }, [closeNfzMfaModal]);

   const handleLogin = useCallback(
      async (mfaTotp: string, setError: NfzMfaTotpErrorSetter): Promise<void> => {
         if (!onLoginCallback) {
            return;
         }

         try {
            await onLoginCallback(mfaTotp);
            closeMfaModal();
         } catch (error) {
            if (isNfzMfaLoginFault(error)) {
               setError(nfzMfaTranslationKeys.invalidCodeError);
               return;
            }

            closeMfaModal();
         }
      },
      [onLoginCallback, closeMfaModal]
   );

   const handleCancel = useCallback((): void => {
      setOnLoginCallback(null);
   }, []);

   const renderModal = useCallback(
      (): ReactElement | null =>
         renderNfzMfaModal({
            onLogin: handleLogin,
            onCancel: handleCancel,
            blockingOperationLabel: blockingOperationLabel,
         }),
      [renderNfzMfaModal, handleLogin, handleCancel, blockingOperationLabel]
   );

   return {
      checkMfaRequired: isEzwmMfaRequired,
      openMfaModal: openMfaModal,
      renderMfaModal: renderModal,
      mfaModalOpened: nfzMfaModalOpened,
   };
};
